import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Save } from 'lucide-react';
import {
  compactSelectClass,
  fieldClass,
  labelClass,
  primaryBtnClass,
  secondaryBtnClass,
} from '../styles';
import {
  createProduct,
  fetchCategoryTypes,
  fetchProductCategories,
  fetchProducts,
  updateProduct,
} from './api';
import CoreOutputsEditor from './CoreOutputsEditor';
import {
  CategoryTypeRow,
  emptyProductForm,
  formFromProduct,
  MarketplaceMeta,
  ProductCategoryRow,
  ProductFormValue,
  ProductRow,
} from './types';

interface Props {
  meta: MarketplaceMeta;
}

const ProductsPanel: React.FC<Props> = ({ meta }) => {
  const [types, setTypes] = useState<CategoryTypeRow[]>([]);
  const [categories, setCategories] = useState<ProductCategoryRow[]>([]);
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [typeFilter, setTypeFilter] = useState<number | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<ProductFormValue>(emptyProductForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadLookups = async () => {
    const [typeRows, categoryRows] = await Promise.all([
      fetchCategoryTypes(),
      fetchProductCategories(),
    ]);
    setTypes(typeRows);
    setCategories(categoryRows);
  };

  const loadProducts = async () => {
    const data = await fetchProducts({
      category: categoryFilter || undefined,
      category_type: categoryFilter ? undefined : typeFilter || undefined,
    });
    setRows(data);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadLookups();
        if (!cancelled) await loadProducts();
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load products');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // initial only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const reload = async () => {
      try {
        const data = await fetchProducts({
          category: categoryFilter || undefined,
          category_type: categoryFilter ? undefined : typeFilter || undefined,
        });
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load products');
        }
      }
    };
    void reload();
    return () => {
      cancelled = true;
    };
  }, [typeFilter, categoryFilter]);

  useEffect(() => {
    if (selectedId === 'new') {
      const defaultCategory =
        categoryFilter ||
        categories.find((c) => !typeFilter || c.category_type === typeFilter)?.id ||
        categories[0]?.id ||
        '';
      setForm(
        emptyProductForm(
          defaultCategory,
          meta.service_types[0]?.value || 'Catalyx'
        )
      );
      return;
    }
    if (selectedId == null) return;
    const row = rows.find((r) => r.id === selectedId);
    if (row) setForm(formFromProduct(row));
  }, [selectedId, rows, categoryFilter, categories, typeFilter, meta.service_types]);

  const filteredCategories = useMemo(() => {
    if (!typeFilter) return categories;
    return categories.filter((c) => c.category_type === typeFilter);
  }, [categories, typeFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.product_category_name || '').toLowerCase().includes(q) ||
        (r.service_type || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const save = async () => {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (form.product_category === '') {
      setError('Product category is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (selectedId === 'new') {
        const created = await createProduct(form);
        setRows((prev) => [...prev, created]);
        setSelectedId(created.id);
        setMessage('Product created.');
      } else if (typeof selectedId === 'number') {
        const updated = await updateProduct(selectedId, form);
        setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
        setForm(formFromProduct(updated));
        setMessage('Product saved.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading products…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {(error || message) && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {error || message}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[520px]">
        <div className="lg:col-span-4 border border-[#E8E8E8] rounded-lg overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-[#E8E8E8] space-y-2">
            <div className="flex items-center gap-2">
              <input
                className={`${fieldClass} h-9`}
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className={`${secondaryBtnClass} shrink-0 inline-flex items-center gap-1`}
                onClick={() => {
                  setSelectedId('new');
                  setMessage(null);
                  setError(null);
                }}
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                className={compactSelectClass}
                value={typeFilter}
                onChange={(e) => {
                  const next = e.target.value ? Number(e.target.value) : '';
                  setTypeFilter(next);
                  setCategoryFilter('');
                }}
                aria-label="Filter by category type"
              >
                <option value="">All types</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <select
                className={compactSelectClass}
                value={categoryFilter}
                onChange={(e) =>
                  setCategoryFilter(e.target.value ? Number(e.target.value) : '')
                }
                aria-label="Filter by product category"
              >
                <option value="">All categories</option>
                {filteredCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <ul className="flex-1 max-h-[480px] overflow-y-auto">
            {filtered.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(row.id);
                    setMessage(null);
                    setError(null);
                  }}
                  className={`w-full px-3 py-2.5 text-left text-sm border-b border-[#F0F0F0] ${
                    selectedId === row.id
                      ? 'bg-[#F3F8FC] text-[#0C0B0B] font-medium'
                      : 'text-[#4A4A4A] hover:bg-[#FAFAFA]'
                  }`}
                >
                  <div>{row.name}</div>
                  <div className="text-[11px] text-[#8A8A8A] mt-0.5">
                    {row.product_category_name || '—'}
                    {` · ${row.service_type} · ${row.credits} cr`}
                  </div>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-6 text-sm text-[#8A8A8A]">No products</li>
            )}
          </ul>
        </div>

        <div className="lg:col-span-8 border border-[#E8E8E8] rounded-lg p-4">
          {selectedId == null ? (
            <div className="h-full min-h-[280px] flex items-center justify-center text-sm text-[#8A8A8A] border border-dashed border-[#E0E0E0] rounded-lg">
              Select a product or click Add.
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-[#0C0B0B]">
                  {selectedId === 'new' ? 'New product' : 'Edit product'}
                </h3>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void save()}
                  className={`${primaryBtnClass} inline-flex items-center gap-1.5`}
                >
                  <Save className="w-3.5 h-3.5" />
                  Save
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelClass}>Product category</label>
                  <select
                    className={fieldClass}
                    value={form.product_category}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        product_category: e.target.value ? Number(e.target.value) : '',
                      })
                    }
                  >
                    <option value="">Select category…</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.category_type_name ? ` (${c.category_type_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Name</label>
                  <input
                    className={fieldClass}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Short description</label>
                  <textarea
                    className={`${fieldClass} min-h-[64px]`}
                    value={form.short_description}
                    onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Long description</label>
                  <textarea
                    className={`${fieldClass} min-h-[100px]`}
                    value={form.long_description}
                    onChange={(e) => setForm({ ...form, long_description: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Service type</label>
                  <select
                    className={fieldClass}
                    value={form.service_type}
                    onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                  >
                    {meta.service_types.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Display order</label>
                  <input
                    type="number"
                    className={fieldClass}
                    value={form.display_order}
                    onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Credits</label>
                  <input
                    type="number"
                    className={fieldClass}
                    value={form.credits}
                    onChange={(e) => setForm({ ...form, credits: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Duration (days)</label>
                  <input
                    type="number"
                    className={fieldClass}
                    value={form.duration_in_days}
                    onChange={(e) => setForm({ ...form, duration_in_days: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2 pt-2">
                  <CoreOutputsEditor
                    value={form.core_outputs}
                    configured={meta.blob.configured}
                    container={meta.blob.container}
                    onChange={(core_outputs) => setForm({ ...form, core_outputs })}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsPanel;
